import { useCallback, useEffect, useState } from "react";
import {
  fetchInterviewInfo,
  fetchInterviewRecording,
} from "@/services/interviewApi";

/**
 * One request per room and endpoint for the whole page: the preview, the
 * call and the leave page share the answer instead of asking again (the old
 * app asked up to seven times per visit). A failed request is forgotten so
 * `retry` can ask again.
 */
const cache = new Map();

function load(key, fetcher) {
  if (!cache.has(key)) {
    const promise = fetcher().catch(error => {
      cache.delete(key);
      throw error;
    });
    cache.set(key, promise);
  }
  return cache.get(key);
}

export function clearInterviewCache() {
  cache.clear();
}

function usePlatformData(key, fetcher) {
  const [state, setState] = useState({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!key) {
      return undefined;
    }
    let cancelled = false;
    setState(current =>
      current.status === "ready" ? current : { status: "loading" }
    );
    load(key, fetcher)
      .then(data => {
        if (!cancelled) {
          setState({ status: "ready", data });
        }
      })
      .catch(error => {
        if (!cancelled) {
          setState({ status: "error", error });
        }
      });
    return () => {
      cancelled = true;
    };
    // fetcher is derived from key
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  const retry = useCallback(() => setAttempt(value => value + 1), []);

  return { ...state, retry };
}

/** `{ status: "loading" | "ready" | "error", data, error, retry }` */
export function useInterviewInfo(roomId) {
  return usePlatformData(roomId && `info:${roomId}`, () =>
    fetchInterviewInfo(roomId)
  );
}

/** `{ status, data: { recording_enabled }, error, retry }` */
export function useInterviewRecording(roomId) {
  return usePlatformData(roomId && `recording:${roomId}`, () =>
    fetchInterviewRecording(roomId)
  );
}
